import { useTranslation } from 'react-i18next';

import type { GameCode, ProfileCard } from '@/types/qriots';
import type { ClassifiedQriotsError } from '@/services/qriotsErrors';

import { LolProfileCard } from './LolProfileCard';
import { TftProfileCard } from './TftProfileCard';
import { LorProfileCard } from './LorProfileCard';
import { QriotsErrorState } from './QriotsErrorState';
import type { ComputedDeltas, DeltaStat } from './compareDeltas';

interface ComparedProfileProps {
  side: 'A' | 'B';
  game: GameCode;
  data?: ProfileCard;
  error?: ClassifiedQriotsError;
  deltas: ComputedDeltas;
  riotIdHint: string;
  onRetry?: () => void;
}

export function ComparedProfile({
  side,
  game,
  data,
  error,
  deltas,
  riotIdHint,
  onRetry,
}: ComparedProfileProps) {
  const { t } = useTranslation('riot');
  const wins = deltas[side];

  if (error) {
    return (
      <div className="flex flex-col gap-2">
        <p className="mx-4 text-xs font-bold uppercase tracking-wider text-fg-muted">
          {t(side === 'A' ? 'compare.sideA' : 'compare.sideB')} · {riotIdHint}
        </p>
        <QriotsErrorState
          kind={error.kind}
          retryAfterSec={error.retryAfterSec}
          onRetry={onRetry}
        />
      </div>
    );
  }

  if (!data) return null;

  const statLabel = (s: DeltaStat) => t(`compare.delta.${s}`);

  return (
    <div className="flex flex-col gap-2">
      {wins.length > 0 && (
        <div className="mx-4 flex items-center gap-1.5 rounded-lg bg-brand/15 px-3 py-1.5 text-xs font-medium text-brand">
          <span aria-hidden>▲</span>
          {t('compare.delta.leadsIn', {
            stats: wins.map(statLabel).join(' · '),
          })}
        </div>
      )}
      {game === 'lol' && <LolProfileCard data={data} />}
      {game === 'tft' && <TftProfileCard data={data} />}
      {game === 'lor' && <LorProfileCard data={data} />}
    </div>
  );
}
